import type { Analytix } from '~/shared/analytix'
import type { Gate } from './gate'
import type { GeneralParameters } from './parameters/general'
import type { ShuntingItem } from './shunting-queue'
import type { SnapshotMap } from './snapshot-map'

import { F, T } from '~/shared/helpers'
import { parameters } from './parameters/general'

export const DEFAULT_TIMEOUT = 5000 // 5s

export type AnyAnalytixEvent = AnalytixEvent<any>

export abstract class AnalytixEvent<
  T extends Record<string, unknown>,
> implements ShuntingItem {
  abstract name: string
  abstract group: string | null
  abstract sender: (params: T) => Promise<boolean>
  abstract gates: Gate[]
  quitGates?: Gate[]

  coalescing = false
  flushing?: string[]

  general = parameters
  abstract parameters: SnapshotMap<T>

  generalView?: GeneralParameters
  parametersView?: T

  created = Date.now()
  timeout = DEFAULT_TIMEOUT

  resolve: () => void
  reject: () => void

  readiness: Promise<boolean>
  ready = false
  failed = false
  cancelled = false
  flushed = false

  constructor() {
    const { promise, resolve, reject } = Promise.withResolvers<void>()
    this.readiness = promise.then(T, F)
    this.resolve = resolve
    this.reject = reject
  }

  get expired(): boolean {
    if (Date.now() - this.created > this.timeout) {
      this.abort() // auto-abort on expiry
      return true
    }
    return false
  }

  done(): void {
    // event cannot be "ready" if already "failed"
    if (!this.failed) {
      this.ready = true
      this.resolve()
    }
  }

  fail(): void {
    // but event CAN be "failed" even if already "ready"
    // reject() will do nothing in that case, but `failed` flag will still be set
    this.failed = true
    this.reject()
  }

  abort(): void {
    this.fail()
  }

  cancel(): void {
    this.cancelled = true
    this.fail()
  }

  flush(): void {
    this.flushed = true
    this.verify().then((ready) => {
      if (!ready && !this.ready && !this.failed) {
        // if still not ready after verification - forcefully set views and mark as done
        this.generalView = Object.fromEntries(this.general)
        this.parametersView = Object.fromEntries(this.parameters) as T
        this.done()
      }
    })
  }

  /**
   * Validate parameters for the event, and returns snapshot views if validation is successful,
   * or `false` if validation fails. This is needed to ensure that the event is sent only when all
   * required parameters are available and valid, and to freeze parameters at the moment of validation.
   */
  abstract validate(): false | [GeneralParameters, T]

  /**
   * Verify event parameters for readiness. This is only method event must call to update its readiness state.
   */
  async verify(): Promise<boolean> {
    // ensure async execution to allow parameters to be set before validation
    await Promise.resolve()

    const validation = this.validate()
    if (validation !== false) {
      this.generalView = validation[0]
      this.parametersView = validation[1]
      this.done()
      return true
    }

    return false
  }

  /**
   *
   * @param analytix - Analytix instance to send the event with.
   */
  send(analytix: Analytix): Promise<boolean> {
    const userInfo = this.generalView ?? Object.fromEntries(this.general)
    const params =
      this.parametersView ?? (Object.fromEntries(this.parameters) as T)

    analytix.userInfo = userInfo
    return this.sender.call(analytix, params)
  }
}
